import React, { useEffect, useRef, useState } from 'react';
import {
  ActionDrawerItem,
  ActionMenu,
  Alert,
  Icon,
  IconButton,
  PageHeading,
  Pills,
  Table,
  Text,
} from 'barte-design-system';
import type { ColumnDefinition, PillsState } from 'barte-design-system';
import {
  ArrowUp,
  Sparkle,
} from '@phosphor-icons/react';
import { PortalPayfacsLayout } from '../PortalPayfacsLayout';
import './BuscaChat.css';

// ─── Types ────────────────────────────────────────────────────────────────────

type TransacaoStatus = 'Aprovada' | 'Pendente' | 'Cancelada' | 'Estornada';

interface Transacao {
  id: string;
  valor: string;
  data: string;
  seller: string;
  status: TransacaoStatus;
}

type MessageRole = 'user' | 'ai';

type AiMessageContent =
  | { type: 'text'; text: string }
  | { type: 'results'; intro: string; results: Transacao[] }
  | { type: 'empty'; followUp: string }
  | { type: 'error' };

interface Message {
  id: string;
  role: MessageRole;
  text?: string;
  aiContent?: AiMessageContent;
  loading?: boolean;
}

// ─── Mock data ────────────────────────────────────────────────────────────────

const MOCK_RESULTS_PENDENTES: Transacao[] = [
  { id: 'TRX-0483', valor: 'R$ 1.240,00', data: '07/04/2026', seller: 'Restaurante do João',   status: 'Pendente'  },
  { id: 'TRX-0491', valor: 'R$ 380,50',   data: '07/04/2026', seller: 'Loja da Maria Moda',    status: 'Pendente'  },
  { id: 'TRX-0502', valor: 'R$ 5.870,00', data: '06/04/2026', seller: 'Academia FitLife',      status: 'Pendente'  },
];

const MOCK_RESULTS_ESTORNADAS: Transacao[] = [
  { id: 'TRX-0317', valor: 'R$ 8.400,00', data: '01/04/2026', seller: 'Loja da Maria Moda',    status: 'Estornada' },
  { id: 'TRX-0295', valor: 'R$ 2.150,00', data: '28/03/2026', seller: 'Auto Peças Central',    status: 'Estornada' },
];

const MOCK_RESULTS_JOAO: Transacao[] = [
  { id: 'TRX-0483', valor: 'R$ 1.240,00', data: '07/04/2026', seller: 'Restaurante do João',   status: 'Pendente'  },
  { id: 'TRX-0469', valor: 'R$ 187,50',   data: '05/04/2026', seller: 'Restaurante do João',   status: 'Aprovada'  },
  { id: 'TRX-0447', valor: 'R$ 64,00',    data: '03/04/2026', seller: 'Restaurante do João',   status: 'Aprovada'  },
];

// ─── Simulate AI responses ────────────────────────────────────────────────────

function simulateAiResponse(query: string): AiMessageContent {
  const q = query.toLowerCase();

  if (q.includes('pendente') || q.includes('pendentes')) {
    return {
      type: 'results',
      intro: `Encontrei ${MOCK_RESULTS_PENDENTES.length} transações pendentes:`,
      results: MOCK_RESULTS_PENDENTES,
    };
  }
  if (q.includes('estorn')) {
    return {
      type: 'results',
      intro: `Encontrei ${MOCK_RESULTS_ESTORNADAS.length} transações estornadas:`,
      results: MOCK_RESULTS_ESTORNADAS,
    };
  }
  if (q.includes('joão') || q.includes('joao') || q.includes('restaurante')) {
    return {
      type: 'results',
      intro: `Encontrei ${MOCK_RESULTS_JOAO.length} transações do Restaurante do João:`,
      results: MOCK_RESULTS_JOAO,
    };
  }
  if (q.includes('erro') || q.includes('falha')) {
    return { type: 'error' };
  }

  return {
    type: 'empty',
    followUp:
      'Não encontrei transações com esses critérios. Você sabe o período aproximado? Ou tem o ID da transação?',
  };
}

// ─── Status → Pills ───────────────────────────────────────────────────────────

const STATUS_PILL: Record<TransacaoStatus, PillsState> = {
  Aprovada:  'success',
  Pendente:  'warning',
  Cancelada: 'default',
  Estornada: 'error',
};

// ─── Actions per status ───────────────────────────────────────────────────────

function getActions(status: TransacaoStatus, id: string) {
  const verDetalhe = { label: 'Ver detalhe', onClick: () => alert(`Detalhe: ${id}`) };
  switch (status) {
    case 'Aprovada':  return [verDetalhe, { label: 'Estornar',   onClick: () => alert(`Estornar: ${id}`) }];
    case 'Pendente':  return [verDetalhe, { label: 'Cancelar',   onClick: () => alert(`Cancelar: ${id}`) }];
    case 'Cancelada': return [verDetalhe];
    case 'Estornada': return [verDetalhe, { label: 'Contestar',  onClick: () => alert(`Contestar: ${id}`) }];
  }
}

// ─── Table columns ────────────────────────────────────────────────────────────

const COLUMNS: ColumnDefinition<Transacao>[] = [
  {
    key: 'id',
    title: 'ID',
    dataIndex: 'id',
    render: (value) => <Text variant="body-sm">{value as string}</Text>,
  },
  {
    key: 'valor',
    title: 'Valor',
    dataIndex: 'valor',
    render: (value) => <Text variant="label-sm">{value as string}</Text>,
  },
  {
    key: 'data',
    title: 'Data',
    dataIndex: 'data',
    render: (value) => <Text variant="body-sm">{value as string}</Text>,
  },
  {
    key: 'seller',
    title: 'Seller',
    dataIndex: 'seller',
    render: (value) => <Text variant="body-sm">{value as string}</Text>,
  },
  {
    key: 'status',
    title: 'Status',
    dataIndex: 'status',
    render: (value) => (
      <Pills
        label={value as string}
        state={STATUS_PILL[value as TransacaoStatus]}
        size="sm"
      />
    ),
  },
  {
    key: 'acoes',
    title: 'Ações',
    dataIndex: 'id',
    render: (id, record) => {
      const actions = getActions((record as Transacao).status, id as string);
      return (
        <ActionMenu align="right">
          {actions.map((a) => (
            <ActionDrawerItem key={a.label} label={a.label} onClick={a.onClick} />
          ))}
        </ActionMenu>
      );
    },
  },
];

// ─── ChatInput ────────────────────────────────────────────────────────────────

interface ChatInputProps {
  value: string;
  onChange: (v: string) => void;
  onSend: () => void;
  disabled?: boolean;
}

function ChatInput({ value, onChange, onSend, disabled }: ChatInputProps) {
  function handleKeyDown(e: React.KeyboardEvent<HTMLTextAreaElement>) {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      onSend();
    }
  }

  return (
    <div className="chat-input-box">
      <textarea
        className="chat-input-textarea"
        placeholder="Ex: transação do seller João de ontem que ficou pendente"
        value={value}
        rows={1}
        onChange={(e) => {
          onChange(e.target.value);
          // auto-grow
          e.target.style.height = 'auto';
          e.target.style.height = `${e.target.scrollHeight}px`;
        }}
        onKeyDown={handleKeyDown}
        disabled={disabled}
      />
      <div className="chat-input-actions">
        <IconButton
          variant="fill"
          size="base"
          round="soft"
          disabled={!value.trim() || disabled}
          onClick={onSend}
          aria-label="Enviar"
        >
          <Icon icon={ArrowUp} size={18} weight="bold" />
        </IconButton>
      </div>
    </div>
  );
}

// ─── Suggestion chips ─────────────────────────────────────────────────────────

const SUGGESTIONS = [
  'Transações aprovadas neste mês',
  'Quais transações foram estornadas ontem?',
  'Transações de POS rejeitadas hoje',
];

// ─── Component ────────────────────────────────────────────────────────────────

let msgCounter = 0;
function newId() { return `msg-${++msgCounter}`; }

export function BuscaChat() {
  const [messages, setMessages] = useState<Message[]>([]);
  const [inputValue, setInputValue] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  // Auto-scroll to bottom when messages change
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  function handleSend(query?: string) {
    const text = (query ?? inputValue).trim();
    if (!text || isLoading) return;

    const userMsg: Message = { id: newId(), role: 'user', text };
    const loadingMsg: Message = { id: newId(), role: 'ai', loading: true };

    setMessages((prev) => [...prev, userMsg, loadingMsg]);
    setInputValue('');
    setIsLoading(true);

    setTimeout(() => {
      const aiContent = simulateAiResponse(text);
      setMessages((prev) =>
        prev.map((m) => (m.loading ? { ...m, loading: false, aiContent } : m)),
      );
      setIsLoading(false);
    }, 1200);
  }

  const isEmpty = messages.length === 0;

  return (
    <PortalPayfacsLayout activeItem="busca-chat">
      <main className="chat-main">

          {/* ── Estado inicial: input centralizado ─────────────────────── */}
          {isEmpty && (
            <div className="chat-centered">
              <div className="chat-centered-inner">
                <div className="chat-centered-heading">
                  <Text variant="body-xl" className="chat-centered-title">Busca de transações</Text>
                  <Text variant="body-sm" className="chat-centered-description">
                    Descreva a transação que você procura.
                  </Text>
                </div>

                <ChatInput
                  value={inputValue}
                  onChange={setInputValue}
                  onSend={handleSend}
                  disabled={isLoading}
                />

                <div className="chat-suggestions">
                  {SUGGESTIONS.map((s) => (
                    <button
                      key={s}
                      className="chat-suggestion-chip"
                      onClick={() => handleSend(s)}
                      disabled={isLoading}
                    >
                      <Text variant="body-sm">{s}</Text>
                    </button>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* ── Estado com mensagens: heading + bubbles + footer ──────── */}
          {!isEmpty && (
            <>
              <div className="chat-heading">
                <PageHeading title="Busca de transações" />
              </div>

              <div className="chat-messages">
                <div className="chat-bubbles">
                  {messages.map((msg) => (
                    <div
                      key={msg.id}
                      className={`chat-bubble-row ${msg.role === 'user' ? 'chat-bubble-row--user' : 'chat-bubble-row--ai'}`}
                    >
                      {msg.role === 'ai' && (
                        <Pills
                          label="Barte IA"
                          icon={Sparkle}
                          variant="light"
                          state="accent"
                          size="sm"
                        />
                      )}
                      <div className={`chat-bubble ${msg.role === 'user' ? 'chat-bubble--user' : 'chat-bubble--ai'}`}>

                        {/* User message */}
                        {msg.role === 'user' && (
                          <Text variant="body-base">{msg.text}</Text>
                        )}

                        {/* AI — loading */}
                        {msg.role === 'ai' && msg.loading && (
                          <div className="chat-loading-dots">
                            <span /><span /><span />
                          </div>
                        )}

                        {/* AI — text only */}
                        {msg.role === 'ai' && !msg.loading && msg.aiContent?.type === 'text' && (
                          <Text variant="body-base">{(msg.aiContent as { type: 'text'; text: string }).text}</Text>
                        )}

                        {/* AI — results with table */}
                        {msg.role === 'ai' && !msg.loading && msg.aiContent?.type === 'results' && (() => {
                          const c = msg.aiContent as { type: 'results'; intro: string; results: Transacao[] };
                          return (
                            <div className="chat-bubble-results">
                              <Text variant="body-base">{c.intro}</Text>
                              <div className="chat-table-wrapper">
                                <Table<Transacao>
                                  data={c.results}
                                  columns={COLUMNS}
                                  rowKey={(r) => r.id}
                                  size="sm"
                                />
                              </div>
                            </div>
                          );
                        })()}

                        {/* AI — sem resultado */}
                        {msg.role === 'ai' && !msg.loading && msg.aiContent?.type === 'empty' && (() => {
                          const c = msg.aiContent as { type: 'empty'; followUp: string };
                          return (
                            <Text variant="body-base">{c.followUp}</Text>
                          );
                        })()}

                        {/* AI — error */}
                        {msg.role === 'ai' && !msg.loading && msg.aiContent?.type === 'error' && (
                          <Alert
                            variant="error"
                            title="Não foi possível completar a busca."
                            description="Ocorreu um erro no serviço. Tente novamente em instantes."
                            showCloseButton={false}
                          />
                        )}
                      </div>
                    </div>
                  ))}
                  <div ref={messagesEndRef} />
                </div>
              </div>

              <div className="chat-footer">
                <ChatInput
                  value={inputValue}
                  onChange={setInputValue}
                  onSend={handleSend}
                  disabled={isLoading}
                />
              </div>
            </>
          )}

      </main>
    </PortalPayfacsLayout>
  );
}
